import { useEffect, useRef, useState } from 'react';
import { allowedMethods, createMerchantSecurityClient, preferredMethod, securityErrorMessage } from './merchantSecurity';
import SecurityChallenge from './SecurityChallenge';

const methodName = method => method === 'PIN' ? 'PIN' : '이메일';
function MethodSelect({ label, value, options, onChange }) {
    return <label style={{ display: 'block', marginTop: 12 }}>{label}
        <select className="form-control mt-2" value={value} onChange={e => onChange(e.target.value)}>
            {!value && <option value="">선택해주세요</option>}
            {options.map(method => <option key={method} value={method}>{methodName(method)}</option>)}
        </select>
    </label>;
}
export default function SecurityLogin({ initialCode, onComplete, onRestart }) {
    const [client] = useState(() => createMerchantSecurityClient());
    const [settings, setSettings] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [loginMethod, setLoginMethod] = useState('');
    const [withdrawMethod, setWithdrawMethod] = useState('OFF');
    const [managementMethod, setManagementMethod] = useState('');
    const [password, setPassword] = useState('');
    const [pin, setPin] = useState('');
    const [repeatPin, setRepeatPin] = useState('');
    const [email, setEmail] = useState('');
    const [emailProof, setEmailProof] = useState(null);
    const [manageProof, setManageProof] = useState(null);
    const [generation, setGeneration] = useState(0);
    const completed = useRef(null);
    const locked = useRef(false);
    const alive = useRef(true);
    const clearProofs = () => { setEmailProof(null); setManageProof(null); setPassword(''); setPin(''); setRepeatPin(''); setGeneration(value => value + 1); };
    const load = async () => {
        if (locked.current) return;
        locked.current = true; setLoading(true); setSettings(null); setError(''); clearProofs();
        try {
            const data = await client.settings();
            if (!alive.current) return;
            if (completed.current) { onComplete(completed.current); return; }
            setSettings(data);
            setLoginMethod(preferredMethod(data.login, data.preferences?.login));
            setWithdrawMethod(data.withdraw.required ? preferredMethod(data.withdraw, data.preferences?.withdraw) : 'OFF');
            setManagementMethod(data.pin_registered ? 'PIN' : data.email_verified ? 'EMAIL' : '');
        } catch (failure) { if (alive.current) setError(securityErrorMessage(failure)); }
        finally { locked.current = false; if (alive.current) setLoading(false); }
    };
    const loader = useRef(load); loader.current = load;
    useEffect(() => {
        alive.current = true; void loader.current();
        return () => { alive.current = false; client.clear(); };
    }, [client]);
    const sessionError = failure => {
        clearProofs(); setSettings(null); client.clear(); setError(securityErrorMessage(failure));
    };
    const complete = async proof => {
        if (locked.current) return;
        if (proof.expiresAt <= Date.now()) throw new Error('인증 권한이 만료되었습니다. 다시 인증해주세요.');
        locked.current = true; setLoading(true);
        try {
            const data = await client.post('/login/complete', { authorization: proof.value });
            completed.current = data || {};
            // Session rotation invalidates the old CSRF token; refresh before entering the app.
            await client.settings();
            if (alive.current) onComplete(completed.current);
        } catch (failure) { if (alive.current) { setSettings(null); setError(securityErrorMessage(failure)); } }
        finally { locked.current = false; if (alive.current) setLoading(false); }
    };
    const chosen = [loginMethod, withdrawMethod];
    const needsPin = chosen.includes('PIN') && !settings?.pin_registered;
    const needsEmail = chosen.includes('EMAIL') && !settings?.email_verified;
    const needsManage = Boolean(settings?.pin_registered || settings?.email_verified);
    const enrollment = initialCode === 4281 || (loginMethod === 'PIN' && !settings?.pin_registered) || (loginMethod === 'EMAIL' && !settings?.email_verified);
    const blocked = settings?.recovery_required || Number(settings?.locked_until) > Date.now();
    const save = async event => {
        event.preventDefault();
        if (locked.current || !settings?.enabled || blocked) return;
        if (!password || !allowedMethods(settings.login).includes(loginMethod)
            || (settings.withdraw.required && !allowedMethods(settings.withdraw).includes(withdrawMethod))) {
            setError('현재 비밀번호와 정책에 맞는 인증 방법을 입력해주세요.'); return;
        }
        if (needsPin && (!/^[0-9]{4}$/.test(pin) || pin !== repeatPin)) { setError('새 PIN 4자리와 확인 값이 일치해야 합니다.'); return; }
        if ((needsEmail && (!emailProof || emailProof.expiresAt <= Date.now())) || (needsManage && (!manageProof || manageProof.expiresAt <= Date.now()))) {
            setEmailProof(null); setManageProof(null); setError('필요한 이메일·기존 인증을 완료해주세요. 만료되었다면 다시 인증해주세요.'); return;
        }
        const body = { expected_version: settings.version, password, preferences: { login: loginMethod, withdraw: settings.withdraw.required ? withdrawMethod : 'OFF' } };
        if (needsPin) body.pin = pin;
        if (needsEmail) body.email_authorization = emailProof.value;
        if (needsManage) body.management_authorization = manageProof.value;
        locked.current = true; setLoading(true); setError('');
        try {
            await client.post('/credentials', body);
            client.clear(); clearProofs();
            if (alive.current) onRestart('인증수단 등록·선택을 저장했습니다. 아이디와 비밀번호로 다시 로그인해주세요.');
        } catch (failure) {
            if ([4091, 4032, 9999, 4230, 5033].includes(failure?.code)) sessionError(failure);
            else { setError(securityErrorMessage(failure)); clearProofs(); }
        } finally { locked.current = false; if (alive.current) setLoading(false); }
    };
    return <div aria-busy={loading} style={{ maxWidth: 420, maxHeight: '80vh', overflowY: 'auto' }}>
        <h2 className="font-bold text-2xl">{enrollment ? '인증수단 등록·선택' : '로그인 추가 인증'}</h2>
        {loading && <p role="status">인증 정보를 확인하고 있습니다.</p>}
        {error && <p role="alert" className="mt-4">{error}</p>}
        {settings?.enabled === false && <p role="alert">추가 인증이 비활성화되었습니다. 로그인 화면으로 돌아가 다시 로그인해주세요.</p>}
        {blocked && <p role="alert">{settings.recovery_required ? '관리자 본인 확인과 복구가 필요합니다. 일반 등록으로 진행할 수 없습니다.' : `인증이 잠겼습니다. 해제 예정: ${new Date(settings.locked_until).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}`}</p>}
        {settings?.enabled && !blocked && !loading && <>
            {allowedMethods(settings.login).length === 0 ? <p role="alert">로그인 인증 방법을 확인할 수 없습니다. 설정을 다시 조회해주세요.</p> : <>
                <MethodSelect label="로그인 인증 방법" value={loginMethod} options={allowedMethods(settings.login)} onChange={value => { setLoginMethod(value); clearProofs(); }} />
                {!enrollment ? <SecurityChallenge key={`${generation}:login:${loginMethod}`} client={client} purpose="login" method={loginMethod} onVerified={complete} onSessionError={sessionError} /> : <>
                    <p className="mt-4">기존 로그인 PIN은 승계되지 않습니다. 새 인증수단을 등록해주세요.</p>
                    {settings.withdraw.required && <MethodSelect label="송금 인증 방법" value={withdrawMethod} options={allowedMethods(settings.withdraw)} onChange={value => { setWithdrawMethod(value); clearProofs(); }} />}
                    {needsManage && <section className="mt-4">
                        <h3>기존 인증수단 확인</h3>
                        <MethodSelect label="기존 인증 방법" value={managementMethod} options={[settings.pin_registered && 'PIN', settings.email_verified && 'EMAIL'].filter(Boolean)} onChange={value => { setManagementMethod(value); setManageProof(null); }} />
                        {manageProof ? <p>기존 인증 확인 완료</p> : <SecurityChallenge key={`${generation}:manage:${managementMethod}`} client={client} purpose="manage" method={managementMethod} onVerified={setManageProof} onSessionError={sessionError} />}
                    </section>}
                    {needsEmail && <section className="mt-4">
                        <label>인증 이메일<input className="form-control mt-2" type="email" value={email} placeholder={settings.pending_email_masked || '이메일 주소 입력'} onChange={e => { setEmail(e.target.value); setEmailProof(null); }} /></label>
                        {settings.pending_email_available && <p>비워두면 관리자가 지정한 {settings.pending_email_masked} 주소로 발송합니다.</p>}
                        {emailProof ? <p>이메일 확인 완료</p> : <SecurityChallenge key={`${generation}:email:${email}`} client={client} purpose="enroll_email" method="EMAIL" email={email} onVerified={setEmailProof} onSessionError={sessionError} />}
                    </section>}
                    <form onSubmit={save} className="mt-4">
                        {needsPin && <>
                            <label>새 PIN 4자리<input className="form-control mt-2" type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={pin} onChange={e => setPin(e.target.value.replace(/[^0-9]/g, ''))} /></label>
                            <label>새 PIN 확인<input className="form-control mt-2" type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={repeatPin} onChange={e => setRepeatPin(e.target.value.replace(/[^0-9]/g, ''))} /></label>
                            <p>반복되거나 연속된 숫자는 사용할 수 없습니다.</p>
                        </>}
                        <label>현재 로그인 비밀번호<input className="form-control mt-2" type="password" autoComplete="off" value={password} onChange={e => setPassword(e.target.value)} /></label>
                        <button type="submit" className="btn btn-primary mt-4" disabled={loading}>등록·선택 저장</button>
                    </form>
                </>}
            </>}
        </>}
        <div className="mt-4" style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-outline-secondary" disabled={loading} onClick={load}>설정 다시 조회</button>
            <button type="button" className="btn btn-outline-secondary" disabled={loading} onClick={() => { client.clear(); onRestart(''); }}>로그인 화면으로</button>
        </div>
    </div>;
}
